'use client'

import {LinkInput} from '@local/types'
import {Button, Locale, Lottie} from '@local/ui/components'
import {cx} from '@local/utils/cx'
import {useRef} from 'react'
import {AboutText} from './AboutText'
import {useDevice, useIsomorphicLayoutEffect, useScrollRatio} from '@local/hooks'
import {AnimationControls, TimelineDefinition, animate, timeline} from 'motion'
import {lerp, map, precision} from '@local/utils/maths'
import {cubicIn, cubicOut, elasticInOut, expoOut, quintIn, quintOut} from 'eases'

import ArrowSVG from '@local/assets/svgs/arrow-down.svg'

export interface AboutContentProps {
	className?: string
	title: string
	items: Array<string>
	endTitle: string
	cta: LinkInput
}

export const AboutContent = ({className, title, items, endTitle, cta}:AboutContentProps) => {
	const device = useDevice()
	const backdropRef = useRef<HTMLDivElement>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	const scrollTextRef = useRef<HTMLDivElement>(null)

	const backdropProgress = useRef<number>(0.55)
	const progress = useRef<number>(-1)
	const prevCurrentTime = useRef<number>(0)

	const tl = useRef<AnimationControls>()

	useIsomorphicLayoutEffect(() => {
		const aboutText = [...document.querySelectorAll('.aboutText')]
		const aboutTextChars = aboutText.map(el => [...el.querySelectorAll('.aboutChar')])

		const aboutSequenze = aboutText.map((block, index) => {
			const blockMotionEnterStart = [
				block,
				{opacity: [0, 1], transform: ['scale3d(0.75,0.75,0.75)', 'scale3d(1,1,1)'], visibility: ['hidden', 'visible']},
				{duration: 1.5, easing: cubicOut, at: index === 0 ? '0' : '-1.1', allowWebkitAcceleration: true},
			]

			const blockMotionEnterEnd = [
				block,
				{opacity: 1, transform: 'scale3d(1,1,1)', visibility: 'visible'},
				{duration: 1.5, easing: cubicOut, allowWebkitAcceleration: true},
			]

			const blockMotionLeave = index !== aboutText.length - 1
				? [
					block,
					{opacity: 0, transform: 'scale3d(2,2,2)', visibility: 'hidden'},
					{
						duration: 2,
						at: '-0.5',
						easing: cubicIn,
						opacity: {duration: 1.7, easing: quintOut},
						visibility: {duration: 2},
						allowWebkitAcceleration: true,
					},
				]
				: [block, {opacity: 1}, {duration: 1, at: '<'}]

			const charsMotion = aboutTextChars[index].map((char, innerIndex) => ([
				char,
				{opacity: 1},
				{duration: innerIndex === 0 ? 0.3 : 0.2, at: innerIndex === 0 ? '-1.2' : '-0.1', allowWebkitAcceleration: true},
			]))

			return [
				index === 0 ? blockMotionEnterEnd : blockMotionEnterStart,
				...charsMotion,
				blockMotionLeave,
			]
		}).flat() as TimelineDefinition

		animate(
			scrollTextRef.current as Element,
			{opacity: [0, 1], visibility: ['hidden', 'visible']},
			{duration: 1.5, delay: 2.5, easing: cubicOut, visibility: {duration: 0.05}},
		).finished.then((el) => {
			if (el) {
				const scrollTextArrow = scrollTextRef.current?.getElementsByClassName('scrollTextArrow')[0]
				if (scrollTextArrow) {
					animate(
						scrollTextArrow as Element,
						{y: [0, 5, 0]},
						{duration: 4, easing: elasticInOut, repeat: Infinity, direction: 'alternate'},
					)
				}
			}
		})

		// intro animation
		animate(
			aboutText[0] as Element,
			{opacity: [0, 1], transform: ['scale3d(0.75,0.75,0.75)', 'scale3d(1,1,1)'], visibility: ['hidden', 'visible']},
			{
				duration: 2,
				delay: 0.75,
				easing: expoOut,
				visibility: {duration: 0.05},
			},
		).finished.then((el) => {
			// run timeline when intro animation is finished
			if (el) {
				tl.current = timeline(aboutSequenze)
				tl.current.pause()
			}
		})
	}, [])

	useScrollRatio(containerRef, (ratio, {delta}) => {
		backdropProgress.current += delta / 5000
		if (backdropProgress.current >= 1) {
			backdropProgress.current = 0
		}

		if (!tl.current || progress.current === ratio) return

		const _ratio = map(ratio, 0, 0.95, 0, 1)

		const timelineRatio = precision(_ratio * tl.current.duration, 2)
		tl.current.currentTime = device === 'desktop' ? timelineRatio : lerp(prevCurrentTime.current, timelineRatio, 0.15)
		prevCurrentTime.current = tl.current.currentTime
		progress.current = ratio

		if (scrollTextRef.current) {
			const opacity = quintIn(map(ratio, 0, 0.05, 1, 0))
			scrollTextRef.current.style.opacity = `${opacity}`
			scrollTextRef.current.style.visibility = opacity === 0 ? 'hidden' : 'visible'
		}
	}, {offset: [[0, 0], [1, 1]]})

	return (
		<div className={cx('relative [clip-path:inset(0_0)]', className)}>
			{/* backdrop */}
			<div
				ref={backdropRef}
				className='fixed h-screen w-full bg-grey-4 inset-0 z-0 overflow-clip opacity-100 lg-max:pb-30'>
				<Lottie
					progress={backdropProgress}
					className={cx('lg-max:scale-[1.8]', 'h-full w-full')}
					filePath='lottie/Pulse-lottie-v5-light.json'
					autoplay
					loop />
			</div>

			{/* scroll text animation */}
			<div ref={containerRef} className='z-1' style={{height: `calc(${11 + items.length} * var(--screenHeightMin))`}}>
				<div className='fixed h-screen w-full flex flex-col gap-50 '>

					{/* scroll notice */}
					<div
						ref={scrollTextRef}
						className={cx(
							'absolute bottom-18 left-1/2 -translate-x-1/2',
							'flex flex-col items-center gap-5',
							'text-grey-1 text-9 uppercase tracking-widest font-medium',
							'opacity-0 invisible',
						)}>
						<p><Locale>Scroll to read full story</Locale></p>
						<ArrowSVG role='presentation' width={9} height={9} className='h-9 w-9 scrollTextArrow' />
					</div>

					{/* title */}
					<AboutText
						isSerif
						index={0}
						className='span-w-11 lg:span-w-4'
						textClassName={cx(
							'relative w-full opacity-0 invisible',
							'aboutText',
						)}>
						{title}
					</AboutText>

					{/* text items */}
					{items.map((item, index) => (
						<AboutText
							key={index}
							index={index + 1}
							className='span-w-11 lg:span-w-5'
							textClassName={cx(
								'relative w-full opacity-0 invisible',
								'aboutText',
							)}>
							{item}
						</AboutText>
					))}

					{/* end title */}
					<div className={cx(
						'text-white text-center',
						'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2',
						'grid place-items-center span-w-11 lg:span-w-5',
					)}>
						<div className='relative w-full opacity-0 scale-75 transform aboutText'>
							<h2 className='font-serif text-34 lg:text-60 leading-snug tracking-tight'>{endTitle}</h2>
							<Button linkInput={cta} className='mt-24 lg:mt-34 !text-12' variant='primary-transparent'>{cta.label}</Button>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}
